// src/schemas/usuarioClienteSchema.ts

import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, IdCard, Lock, Mail, User2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@components/button/button";
import { MaskedInputCpfCnpj } from "@components/masked-input-cpf-cnpj";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import { usuarioClienteSchema } from "@pages/admin/gerenciar-usuarios-cliente/components/modal-criar-usuario-do-cliente/schema";

import { useCriarUsuarioCliente } from "@/hooks/usuários/criar-usuarios-clientes";

import { limparDocumento } from "@/utils/format-document";

type FormData = z.infer<typeof usuarioClienteSchema>;

type ModalProps = {
  onClose: () => void;
  clienteId: number;
};

export function ModalCriarUsuarioCliente({ onClose, clienteId }: ModalProps) {
  const { criarUsuarioClienteAsync, isLoading } = useCriarUsuarioCliente();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(usuarioClienteSchema),
  });

  const onSubmit = async (data: FormData) => {
    await criarUsuarioClienteAsync({
      ativo: true,
      cliente_id: clienteId,
      nome: data.nome,
      email: data.email,
      senha: data.senha,
      documento: limparDocumento(data.documento!),
    });

    onClose(); // Fecha o modal após sucesso
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-semibold text-zinc-900">Cadastrar novo usuário</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Nome */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-zinc-700">Nome completo</label>
            <div className="flex h-14 w-full items-center gap-2 rounded-lg border border-zinc-800 px-4">
              <User2 className="text-zinc-400" />
              <input
                {...register("nome")}
                placeholder="Nome usuário"
                className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />
            </div>
            {errors.nome && <p className="text-sm text-red-500">{errors.nome.message}</p>}
          </div>

          {/* E-mail */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-zinc-700">E-mail</label>
            <div className="flex h-14 w-full items-center gap-2 rounded-lg border border-zinc-800 px-4">
              <Mail className="text-zinc-400" />
              <input
                {...register("email")}
                placeholder="Informe o E-mail"
                className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />
            </div>
            {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
          </div>

          {/* Senha */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-zinc-700">Senha</label>
            <div className="flex h-14 w-full items-center gap-2 rounded-lg border border-zinc-800 px-4">
              <Lock className="text-zinc-400" />

              <input
                type={showPassword ? "text" : "password"}
                {...register("senha")}
                placeholder="Informe sua Senha"
                className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />

              {/* Ícone para mostrar / esconder senha */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-zinc-500 transition hover:text-zinc-700"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {errors.senha && <p className="text-sm text-red-500">{errors.senha.message}</p>}
          </div>

          {/* Documento */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-zinc-700">CPF ou CNPJ</label>
            <div className="flex h-14 w-full items-center gap-2 rounded-lg border border-zinc-800 px-4">
              <IdCard className="text-zinc-400" />
              <MaskedInputCpfCnpj
                {...register("documento")}
                placeholder="Digite o CPF ou CNPJ"
                className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />
            </div>
            {errors.documento && <p className="text-sm text-red-500">{errors.documento.message}</p>}
          </div>

          {/* Botões */}
          <div className="mt-4 flex justify-end gap-4 border-t pt-4">
            <Button
              type="button"
              variant="secondary"
              className="px-4"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>

            <Button type="submit" className="px-6" disabled={isLoading}>
              {isLoading ? <SpinnerSimple /> : "Cadastrar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
